import client from './client';
import type { PagedResult } from './anbiyam';

/** Row shape returned by sp_GetFamilyListPaged (grid listing) */
export interface FamilyListItem {
  familyId:         number;
  familyCode:       string;
  familyHead?:      string;
  anbiyamName?:     string;
  zone?:            number;
  phone?:           string;
  headOccupation?:  string | null;
  cemeteryCount?:   number;
  isActive?:        boolean;
  subPendingStatus?: 'Paid up' | 'Pending' | 'Long Pending' | null;
  [key: string]: unknown;
}

/** A person within a family — head, spouse, or child */
export interface FamilyMember {
  memberId?:          number;
  name:               string;
  relationship:       string;
  gender?:            string;
  dob?:               string | null;
  phone?:             string;
  email?:             string;
  occupation?:        string;
  qualification?:     string;
  bloodGroup?:        string;
  marriageDate?:      string | null;
  baptismDate?:       string | null;
  isAdmin?:           boolean;
  isLOM?:             boolean;
  isVDPaul?:          boolean;
  isChoir?:           boolean;
  isCatechismTeacher?: boolean;
  isWomenAssoc?:      boolean;
  isLitergyCouncil?:  boolean;
  isYouth?:           boolean;
  isAlterServices?:   boolean;
  memberGroup?:       string;
  childClass?:        string;
  childInstitution?:  string;
  confirmationDate?:  string | null;
  communionDate?:     string | null;
  priesthoodDate?:    string | null;
  [key: string]: unknown;
}

export interface FamilyDetail {
  family:  Record<string, unknown>;
  members: FamilyMember[];
}

export interface FamilyPersonPayload {
  name:  string;
  phone?: string;
  email?: string;
}

export interface FamilyPayload {
  familyCode:            string;
  anbiyamId:             number;
  permanentAddress?:     string;
  tempAddress?:          string;
  permCity?:             string;
  permState?:            string;
  permZipcode?:          string;
  tempCity?:             string;
  tempState?:            string;
  tempZipcode?:          string;
  monthlySubscription?:  number;
  isMultipleCards?:      boolean;
  familyNotes?:          string;
  lastSubscriptionDate?: string | null;
  headOfFamily:          FamilyPersonPayload;
  spouse?:               FamilyPersonPayload;
  isHusbandActive?:      boolean;
  isWifeActive?:         boolean;
}

export type SubPendingStatus = 'Paid up' | 'Pending' | 'Long Pending';

export const listFamilies = async (params: {
  page?: number; pageSize?: number;
  anbiyamId?: number | null; head?: string; occupation?: string;
  cemeteryAvailable?: boolean | null;
  status?: 'active' | 'inactive';
  familyCode?: string;
  subStatus?: SubPendingStatus;
}): Promise<PagedResult<FamilyListItem>> => {
  const { data } = await client.get<{ data: PagedResult<FamilyListItem> }>('/family', { params });
  return data.data;
};

export const getFamily = async (id: number): Promise<FamilyDetail> => {
  const { data } = await client.get<{ data: FamilyDetail }>(`/family/${id}`);
  return data.data;
};

export interface ChurchPlaceholderFamily {
  familyId:   number;
  familyCode: string;
  familyHead: string;
}

/** The one placeholder family (family_code = 'CHURCH') used to hold cemetery
 *  records whose real family isn't identified yet — see CemeteryPage's
 *  "Not linked to a family yet" flow. */
export const getChurchPlaceholderFamily = async (): Promise<ChurchPlaceholderFamily> => {
  const { data } = await client.get<{ data: ChurchPlaceholderFamily }>('/family/church-placeholder');
  return data.data;
};

export const createFamily = async (payload: FamilyPayload): Promise<{ familyId: number }> => {
  const { data } = await client.post<{ data: { familyId: number } }>('/family', payload);
  return data.data;
};

export const updateFamily = async (id: number, payload: Partial<FamilyPayload>): Promise<unknown> => {
  const { data } = await client.put<{ data: unknown }>(`/family/${id}`, payload);
  return data.data;
};

/** Soft-deactivate (left the parish / moved out) or reactivate a family.
 *  Keeps all history — unlike deleteFamily. */
export const setFamilyStatus = async (id: number, isActive: boolean): Promise<void> => {
  await client.put(`/family/${id}/status`, { isActive });
};

export const deleteFamily = async (id: number): Promise<void> => {
  await client.delete(`/family/${id}`);
};
